import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";

const AiLanding = () => {
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  return (
    <div className="font-sans text-gray-900 bg-black min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Video Background */}
        <video
          ref={videoRef}
          autoPlay
          loop
          muted={isMuted}
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover z-0"
        >
          <source src="/DocLift-V2.2.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Overlay */}
        <div className="absolute top-0 left-0 w-full h-full bg-black/50 z-10"></div>

        {/* Mute/Unmute Button */}
        <button
          onClick={toggleMute}
          className="absolute bottom-10 right-10 z-30 p-3 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full text-white transition-all duration-300"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75 21 12m0 0-3.75 2.25M21 12l-3.75-2.25m-3.75 0-3.75 2.25m-3 0-3.75-2.25M12 12l3.75 2.25" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
              <line x1="1" y1="1" x2="23" y2="23" stroke="white" strokeWidth="2" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
            </svg>
          )}
        </button>

        {/* Content */}
        <div className="relative z-20 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tighter mb-4 animate-fade-in-up">
            AI Communication Platform
          </h1>
          <p className="text-lg md:text-2xl text-gray-200 mb-8 max-w-2xl mx-auto animate-fade-in-up delay-100">
            Automate customer interactions for your business. Chat, WhatsApp, and Voice.
          </p>

          <button
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-full text-xl shadow-lg transition-transform transform hover:scale-105 animate-pulse"
            onClick={() => console.log("Chat Now clicked")}
          >
            Chat Now
          </button>
        </div>
      </section>

      {/* Features Section - Modern Cards/Bento Grid */}
      <section className="py-20 px-4 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-16">
            Designed for Service Businesses
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow border border-gray-700">
              <div className="text-purple-500 text-4xl mb-4">
                <i className="fas fa-robot"></i> {/* Using generic icon placeholder or emoji if fontawesome not available, assuming standard setup */}
                🤖
              </div>
              <h3 className="text-xl font-bold text-white mb-2">24/7 Automation</h3>
              <p className="text-gray-400">
                Never miss an inquiry. Handles appointments, FAQs, and reminders around the clock.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow border border-gray-700">
              <div className="text-purple-500 text-4xl mb-4">
                🏢
              </div>
              <h3 className="text-xl font-bold text-white mb-2">For Local Services</h3>
              <p className="text-gray-400">
                Tailored for dentists, salons, and clinics. Zero technical setup required.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow border border-gray-700">
              <div className="text-purple-500 text-4xl mb-4">
                📈
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Capture More Leads</h3>
              <p className="text-gray-400">
                Save staff time and improve customer experience instantly.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AiLanding;
